// Auth0 Service Integration Component
// Initializes Auth0 token getter for secure API calls

import { useEffect } from 'react';
import { useAuth0 } from './context/Auth0Context';
import { initializeAuth0TokenGetter } from '../services/workflowStorageService';
import { getAuth0Audience, logEnvironmentInfo } from '../utils/environmentDetection';
import logger from '../utils/logger';

export function Auth0ServiceIntegration() {
    const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

    useEffect(() => {
        console.log('🔐 AUTH0-SERVICE: useEffect triggered', {
            isAuthenticated,
            isLoading,
            timestamp: new Date().toISOString()
        });

        // Only initialize when user is authenticated and Auth0 is ready
        if (isAuthenticated && !isLoading) {
            console.log('🔐 AUTH0-SERVICE: Initializing Auth0 token getter for secure API calls');

            // Log environment info for debugging
            logEnvironmentInfo();

            // Create a wrapper function that handles Auth0 token retrieval
            const tokenGetter = async () => {
                const audience = getAuth0Audience();

                console.log('🔐 AUTH0-SERVICE: Token getter called', {
                    audience,
                    timestamp: new Date().toISOString(),
                    isAuthenticated,
                    isLoading
                });

                try {
                    logger.log(`🔐 AUTH0-SERVICE: Requesting token for audience: ${audience}`);

                    // Log the exact parameters being passed to getAccessTokenSilently
                    const tokenParams = {
                        authorizationParams: {
                            audience: audience,
                            scope: 'openid read:workflows write:workflows delete:workflows read:templates write:templates delete:templates read:salts write:salts delete:salts read:storage write:storage delete:storage admin:all'
                        },
                        // Force fresh token request - bypass cache to handle scope expansion
                        cacheLocation: 'memory',
                        useRefreshTokens: false
                    };
                    console.log('🔐 AUTH0-SERVICE: Token request parameters:', tokenParams);

                    const token = await getAccessTokenSilently(tokenParams);

                    logger.log('🔐 AUTH0-SERVICE: Token retrieved successfully for API call');
                    console.log('🔐 AUTH0-SERVICE: Token length:', token ? token.length : 'null');

                    // Log token structure and decode payload (without exposing the actual token)
                    if (token) {
                        try {
                            const tokenParts = token.split('.');
                            console.log('🔐 AUTH0-SERVICE: Token structure:', {
                                parts: tokenParts.length,
                                headerLength: tokenParts[0]?.length,
                                payloadLength: tokenParts[1]?.length,
                                signatureLength: tokenParts[2]?.length
                            });

                            // Decode the JWT payload to inspect claims (safe - no sensitive data exposed)
                            if (tokenParts.length === 3 && tokenParts[1]) {
                                try {
                                    const payload = JSON.parse(atob(tokenParts[1]));
                                    console.log('🔐 AUTH0-SERVICE: Token payload inspection:', {
                                        audience: payload.aud,
                                        scopes: payload.scope,
                                        issuer: payload.iss,
                                        subject: payload.sub,
                                        expiration: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'none',
                                        issuedAt: payload.iat ? new Date(payload.iat * 1000).toISOString() : 'none',
                                        permissions: payload.permissions || 'none',
                                        grantedScopes: payload['https://dev.memer.world/api/scopes'] || 'none',
                                        customClaims: Object.keys(payload).filter(key =>
                                            !['aud', 'scope', 'iss', 'sub', 'exp', 'iat', 'azp', 'gty'].includes(key)
                                        )
                                    });

                                    // Check if the expected audience and scopes are present
                                    const expectedAudience = 'https://dev.memer.world/api';
                                    const expectedScopes = ['read:workflows', 'write:workflows'];

                                    console.log('🔐 AUTH0-SERVICE: Token validation check:', {
                                        hasCorrectAudience: payload.aud === expectedAudience || (Array.isArray(payload.aud) && payload.aud.includes(expectedAudience)),
                                        actualAudience: payload.aud,
                                        expectedAudience,
                                        hasScopes: !!payload.scope,
                                        actualScopes: payload.scope ? payload.scope.split(' ') : [],
                                        expectedScopes,
                                        scopesMatch: expectedScopes.every(scope =>
                                            payload.scope && payload.scope.split(' ').includes(scope)
                                        )
                                    });
                                } catch (decodeError) {
                                    console.log('🔐 AUTH0-SERVICE: Could not decode token payload:', decodeError instanceof Error ? decodeError.message : decodeError);
                                }
                            }
                        } catch (e) {
                            console.log('🔐 AUTH0-SERVICE: Could not parse token structure');
                        }
                    }

                    return token;
                } catch (error: any) {
                    logger.error('❌ AUTH0-SERVICE: Failed to get access token:', error);

                    // Log detailed error information
                    console.log('🔐 AUTH0-SERVICE: Detailed error info:', {
                        error: error.error,
                        error_description: error.error_description,
                        message: error.message,
                        stack: error.stack,
                        audience,
                        isAuthenticated,
                        isLoading,
                        timestamp: new Date().toISOString()
                    });

                    // Enhanced error handling with specific error types
                    if (error.error === 'login_required') {
                        logger.error('❌ AUTH0-SERVICE: Login required - user session may have expired');
                        logger.error('❌ AUTH0-SERVICE: This usually means the Auth0Provider was not initialized with the correct audience');
                        logger.error('❌ AUTH0-SERVICE: Expected audience:', audience);
                        throw new Error('Authentication required. The application was not properly configured for API access. Please refresh the page and try logging in again.');
                    } else if (error.error === 'consent_required') {
                        logger.error('❌ AUTH0-SERVICE: Consent required - user needs to grant API permissions');
                        throw new Error('API permissions required. Please log out and log in again to grant the necessary permissions.');
                    } else if (error.error === 'access_denied') {
                        logger.error('❌ AUTH0-SERVICE: Access denied - insufficient permissions for API');
                        throw new Error('Access denied. Your account does not have the required permissions for this API.');
                    } else {
                        logger.error('❌ AUTH0-SERVICE: Unknown authentication error:', error);
                        logger.error('❌ AUTH0-SERVICE: This suggests the Auth0 application is not authorized for audience:', audience);
                        throw new Error(`Authentication failed: ${error.error_description || error.message || 'Unknown error'}`);
                    }
                }
            };

            // Initialize the service layer with Auth0 token getter
            initializeAuth0TokenGetter(tokenGetter);

            logger.log('✅ AUTH0-SERVICE: Service layer initialized with Auth0 authentication');
            console.log('✅ AUTH0-SERVICE: Initialization complete at', new Date().toISOString());
        } else if (!isLoading) {
            logger.log('⚠️ AUTH0-SERVICE: User not authenticated - API calls will fail until login');
            console.log('⚠️ AUTH0-SERVICE: Auth state:', { isAuthenticated, isLoading });
        } else {
            console.log('🔐 AUTH0-SERVICE: Still loading Auth0 state...');
        }
    }, [isAuthenticated, isLoading, getAccessTokenSilently]);

    // This component doesn't render anything - it's just for initialization
    return null;
}

export default Auth0ServiceIntegration;
